import { photoFileType } from "../workers/kyc";

/**
 * Phone cameras take 3–12 MB photos; a face photo for a worker card needs ~100 KB. Shrink to the
 * longest side {@code max} as JPEG before upload. Formats the browser can't draw (HEIC on some
 * phones) go up as they are. Anything that isn't a photo (a PDF, a video) is refused, so it can
 * never be saved as someone's face photo.
 */
export const NOT_A_PHOTO = "Pick a photo (JPEG or PNG)";

/** Throws {@link NOT_A_PHOTO} for anything that isn't an image (a PDF, a video, an unknown file). */
export const assertPhoto = (file: Blob) => {
  if (!photoFileType(file.type) && !String(file.type || "").toLowerCase().startsWith("image/")) throw new Error(NOT_A_PHOTO);
};

export const shrinkPhoto = async (file: File, max = 800): Promise<{ blob: Blob; type: string }> => {
  assertPhoto(file);
  const type = photoFileType(file.type);
  const original = { blob: file as Blob, type: type || "" };
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    URL.revokeObjectURL(url);
    const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return sendAsIs(original);
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.82));
    return blob ? { blob, type: "image/jpeg" } : sendAsIs(original);
  } catch {
    return sendAsIs(original);
  }
};

/** Unshrunk, only when it is a photo type storage accepts (JPEG, PNG, HEIC). */
const sendAsIs = (original: { blob: Blob; type: string }) => {
  if (!original.type) throw new Error(NOT_A_PHOTO);
  return original;
};
